/**
 * PatientRow - Fila expandible de paciente en resultados de búsqueda
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronDown, User, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { BusquedaResultado } from "@/hooks/use-busqueda-avanzada";
import { OrderRow } from "./order-row.tsx";

interface PatientRowProps {
  paciente: BusquedaResultado;
  onCloseSheet: () => void;
}

/** Calculate age from date of birth string (YYYY-MM-DD) */
function calcularEdad(fechaNacimiento: string): number {
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }
  return edad;
}

export function PatientRow({ paciente, onCloseSheet }: PatientRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const edad = calcularEdad(paciente.fecha_nacimiento);

  const handleViewPaciente = () => {
    onCloseSheet();
    navigate("/pacientes", {
      state: { editPacienteId: paciente.id },
    });
  };

  const handleNewOrden = () => {
    onCloseSheet();
    navigate("/ordenes", {
      state: {
        pacienteId: paciente.id,
        pacienteNombre: paciente.nombre_completo,
        pacienteDesc: [
          paciente.identificacion,
          `${edad} ${t("busquedaAvanzada.results.years")}`,
          paciente.celular ? `Tel: ${paciente.celular}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        pacienteEdadYears: edad,
      },
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3">
          {/* Expand icon */}
          <div className="mt-0.5 text-muted-foreground">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>

          {/* Patient info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{paciente.nombre_completo}</span>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {paciente.sexo_display}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
              <span>{paciente.num_documento}</span>
              <span>
                {edad} {t("busquedaAvanzada.results.years")}
              </span>
              {paciente.celular && <span>{paciente.celular}</span>}
            </div>
          </div>

          {/* Order count badge */}
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {paciente.total_ordenes} {t("busquedaAvanzada.results.ordenes")}
          </Badge>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-3 pl-11">
          {/* Quick actions */}
          <div className="flex gap-2 mb-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleViewPaciente}
            >
              <User className="mr-1 h-3 w-3" />
              {t("busquedaAvanzada.actions.verPaciente")}
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleNewOrden}>
              <ClipboardList className="mr-1 h-3 w-3" />
              {t("busquedaAvanzada.actions.nuevaOrden")}
            </Button>
          </div>

          {/* Orders list */}
          {paciente.ordenes.length > 0 ? (
            <div className="space-y-1">
              {paciente.ordenes.map((orden) => (
                <OrderRow key={orden.id} orden={orden} onCloseSheet={onCloseSheet} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-2">
              {t("busquedaAvanzada.results.noOrdenes")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
